import {Button, ButtonProps} from "@/components/ui/button.tsx";
import {
    MagnifyingGlassIcon,
    MicrophoneIcon,
    PencilIcon,
    PaperClipIcon,
    PlusIcon,
    StopIcon, PlayIcon, TrashIcon, ArrowUturnLeftIcon
} from "@heroicons/react/24/outline";
import React, {useEffect, useRef, useState} from "react";
import {motion} from "framer-motion";
import {api, cn} from "@/lib/utils.ts";
import {ArrowRightCircle, CheckIcon} from "lucide-react";
import {Input} from "@/components/ui/input.tsx";
import {
    StreamFile,
    useCreateStreamFileMutation,
    useGetStreamsFileQuery,
    useSearchStreamFilesQuery
} from "@/store/streams-files.ts";
import {ScrollArea} from "@/components/ui/scroll-area.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {Textarea} from "@/components/ui/textarea.tsx";
import {AudioPlayer} from "@/components/ui/audio-player.tsx";
import {toast} from "sonner";

type Mode = 'record' | 'upload' | 'tts' | 'library';

type UploadAudioFileProps = {
    onChange: (id: number) => void;
    title?: string;
    initialMode?: "upload" | "tts";
}

export function UploadAudioFile(props: UploadAudioFileProps) {

    const {title, onChange, initialMode = 'upload'} = props;

    const [mode, setMode] = useState<Mode>(initialMode);
    const [file, setFile] = useState<File | null>(null);


    function handleChangeWithParentChange(id: number) {
        onChange(id);
        setFile(null);
    }
    return (
        <div className="space-y-2">
            {mode === 'upload' &&
                <PickFileFromComputer
                    onChange={handleChangeWithParentChange}
                    title={title}
                />
            }

            {mode === 'record' && (
                <div className="border-2 flex flex-col gap-2.5 items-center justify-center border-gray-100 border-dashed rounded-lg px-6 py-7">
                    {title && <div className="text-sm font-semibold">{title}</div>}
                    <div className="test-xs text-gray-600">הקלטת שמע</div>
                    <RecordVoice onChange={handleChangeWithParentChange}/>
                </div>
            )}

            {mode === 'tts' && (
                <TtsAudioGenerator onChange={handleChangeWithParentChange} />
            )}

            {mode === 'library' && (
                <SearchStreamFile
                    onChange={handleChangeWithParentChange}
                />
            )}

            <div className="flex justify-between items-center">

                <ButtonMode
                    mode="library"
                    setMode={setMode}
                    currentMode={mode}
                    icon={MagnifyingGlassIcon}
                    label="חפש קובץ"
                />

                <ButtonMode
                    mode="upload"
                    setMode={setMode}
                    currentMode={mode}
                    icon={PaperClipIcon}
                    label="צרף קובץ"
                />
                <ButtonMode
                    mode="record"
                    setMode={setMode}
                    currentMode={mode}
                    icon={MicrophoneIcon}
                    label="הקלט"
                />
                <ButtonMode
                    mode="tts"
                    setMode={setMode}
                    currentMode={mode}
                    icon={PencilIcon}
                    label="המרת טקסט לשמע"
                />

            </div>
        </div>
    )
}

interface ButtonModeProps extends ButtonProps {
    mode: Mode;
    setMode: (mode: Mode) => void;
    currentMode: Mode;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    label: string;
}

function ButtonMode(props: ButtonModeProps) {

    const {mode, setMode, currentMode, icon: Icon, label} = props;

    return (
        <Button
            data-active={currentMode === mode}
            onClick={() => setMode(mode)}
            size="xs"
            className="data-[active=true]:border-light-blue-700 shadow-none border border-gray-200 [&_svg]:text-cyan-70 text-light-blue-700 bg-light-blue-50 hover:bg-light-blue-100 rounded-full [&_svg]:size-5"
        >
            <span>{label}</span>
            <Icon className="h-4 w-4"/>
        </Button>
    )
}

function RecordVoice({ onChange }: { onChange: (id: number) => void }) {
    const [isRecording, setIsRecording] = useState(false);
    const [volume, setVolume] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const dataArrayRef = useRef<Uint8Array | null>(null);
    const animationRef = useRef<number | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [createNewFile, {isLoading}] = useCreateStreamFileMutation();

    const startRecording = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start();
        setIsRecording(true);

        mediaRecorder.ondataavailable = (event) => {
            //save file
            const newFile = new File([event.data], `recording-${Date.now()}.webm`, { type: 'audio/webm' });
            setFile(newFile);
        };

        // Setup audio context and analyser
        audioContextRef.current = new AudioContext();
        analyserRef.current = audioContextRef.current.createAnalyser();
        sourceRef.current = audioContextRef.current.createMediaStreamSource(stream);
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.fftSize = 256;
        const bufferLength = analyserRef.current.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(bufferLength);

        const tick = () => {
            if (analyserRef.current && dataArrayRef.current) {
                analyserRef.current.getByteFrequencyData(dataArrayRef.current);
                const avg =
                    dataArrayRef.current.reduce((sum, val) => sum + val, 0) /
                    dataArrayRef.current.length;
                setVolume(avg);
            }
            animationRef.current = requestAnimationFrame(tick);
        };

        animationRef.current = requestAnimationFrame(tick);
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
        }
    };

    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current?.stream) {
                mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
            }
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
            }
        };
    }, []);

    function handleClick() {
        if(file) {
            return setIsPlaying(s => !s);
        }
        isRecording ? stopRecording() : startRecording()
    }

    const scale = Math.min(2, Math.max(0.5, volume / 64));

    useEffect(() => {
        if (file && isPlaying) {
            const audio = new Audio(URL.createObjectURL(file));
            audio.play();
            audio.onended = () => setIsPlaying(false);
        }
    }, [file, isPlaying]);

    function uploadFileAndChange() {
        if (file) {
            createNewFile({file}).unwrap()
                .then(newFile => onChange(newFile.id))
        }
    }

    return (
        <div className="relative flex items-center justify-center">
            {isRecording && (
                <motion.div
                    className="absolute h-24 w-24 rounded-full bg-red-400"
                    animate={{ scale, opacity: 0.2 }}
                    transition={{ duration: 0.1 }}
                    style={{ zIndex: 1 }}
                />
            )}
            {isRecording || isPlaying && (
                <span
                    className={cn("absolute inset-0 m-auto h-12 w-12 rounded-full opacity-50 animate-ping", {
                        'bg-red-400': isRecording,
                        'bg-light-blue-400': isPlaying,
                    })}
                    style={{
                        transform: `scale(${scale.toFixed(2)})`,
                        transition: 'transform 0.1s linear',
                    }}
                />
            )}
            <button
                onClick={handleClick}
                data-active={isRecording}
                className={cn("h-12 cursor-pointer w-12 rounded-full relative z-10 transition-colors flex items-center justify-center", {
                    'bg-light-blue-600 data-[active=true]:bg-red-600 text-white': !file,
                    'bg-white text-gray-400 border border-gray-300 shadow-xl': file,
                })}>
                {file
                    ? (isPlaying ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />)
                    : (isRecording ? <StopIcon className="h-4 w-4" /> : <MicrophoneIcon className="h-4 w-4" />)}
            </button>

            {file && (
                <>
                    <Button
                        size="icon-xs"
                        variant="destructive"
                        onClick={() => setFile(null)}
                        className="absolute cursor-pointer top-1/2 -translate-y-1/2 ms-2 start-full rounded-full"
                    >
                        <TrashIcon className="h-4 w-4" />
                    </Button>

                    <Button
                        disabled={isLoading}
                        onClick={uploadFileAndChange}
                        size="icon-xs"
                        className="cursor-pointer rounded-full absolute top-1/2 -translate-y-1/2 end-full me-2"
                    >
                        {isLoading ? <Spinner size="sm"/> : <CheckIcon className="h-4 w-4"/>}
                    </Button>
                </>
            )}
        </div>
    );
}

function SearchStreamFile({ onChange }: { onChange: (id: number) => void }) {
    const [search, setSearch] = useState('');
    const [value, setValue] = useState<string>('');
    const [playing, setPlaying] = useState<StreamFile|null>(null);

    const {data, isFetching} = useSearchStreamFilesQuery({query: search}, {
        skip: search.length < 2
    });

    useEffect(() => {
        if(value.length > 2) {
            const timer = setTimeout(() => {
                setSearch(value);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [value]);

    useEffect(() => {
        if(playing) {
            const audio = new Audio(playing.audio_file);
            audio.play();
            audio.onended = () => setPlaying(null);
        }
    }, [playing]);


    return (
        <div>
            <div className="relative">
                {isFetching && <Spinner className="absolute -translate-y-1/2 top-1/2 end-2"/>}

                <Input
                    autoFocus
                    placeholder="הקלד כדי לחפש..."
                    className="w-full"
                    onChange={(e) => setValue(e.target.value)}
                    value={value}
                />
            </div>

            {value.length > 1 ? (
                <ScrollArea className="h-64 mt-2" dir="rtl">
                    <div className="space-y-1">
                        {data && data.results.map( (file) => (
                            <div className="hover:bg-gray-100 ps-2 pe-4 flex py-1.5 rounded-md items-center gap-2">
                                <div>
                                    <Button
                                        onClick={() => setPlaying(currenFile => currenFile?.id === file.id ? null : file)}
                                        variant={playing?.id === file.id ? 'default' : "outline"}
                                        size="icon-xs"
                                        className="cursor-pointer rounded-full"
                                    >
                                        {playing?.id === file.id ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                                    </Button>
                                </div>
                                <div className="flex-grow">
                                    <div className="font-medium text-ellipsis max-w-60 whitespace-nowrap overflow-hidden">{file.name}</div>
                                    <div>
                                        <div className="text-xs text-gray-500">
                                             {file.duration && file.duration.split('.')[0]}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <Button
                                        onClick={() => onChange(file.id)}
                                        size="xs"
                                        variant="outline"
                                        className="cursor-pointer h-5"
                                    >
                                        בחר
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            ) : (
                value.length === 1 ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">הקלד לפחות 2 תווים כדי לחפש</div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">לא נמצאו תוצאות</div>
                    </div>
                )
            )}

        </div>
    )
}

function PickFileFromComputer({ onChange, title }: { onChange: (id: number) => void, title?: string }) {

    const [file, setFile] = useState<File | null>(null);
    const [createNewFile, {isLoading}] = useCreateStreamFileMutation();

    useEffect(() => {
        if (file) {
            createNewFile({file}).unwrap()
                .then(newFile => onChange(newFile.id))
        }
    }, [file]);

    return (
        <div className="relative border-2 flex flex-col gap-2.5 items-center justify-center border-gray-100 border-dashed rounded-lg px-6 py-7">
            <input
                type="file"
                accept="audio/*" className="opacity-0 absolute top-0 left-0 w-full h-full"
                onChange={(e) => {
                    const selectedFile = e.target.files?.[0];
                    if (selectedFile) {setFile(selectedFile)}
                }}
            />
            {title && <div className="text-sm font-semibold">{title}</div>}
            <div className="test-xs text-gray-600">ניתן לגרור או להעלות קובץ שמע</div>
            <Button size="xs" className="cursor-pointer [&_svg]:size-5">
                <PlusIcon className="h-4 w-4"/>
                <span>בחר קובץ</span>
            </Button>

            {isLoading &&
                <div className="absolute top-0 left-0 w-full h-full bg-white/70 flex items-center justify-center rounded-full">
                    <Spinner/>
                </div>
            }
        </div>
    )
}

type TtsAudioGeneratorProps = {
    onChange: (id: number) => void;
    initialText?: string;
}

export function TtsAudioGenerator({ onChange, initialText = '' }: TtsAudioGeneratorProps) {

    const [text, setText]  = useState(initialText);
    const [currentText, setCurrentText] = useState(initialText);
    const [isPlaying, setIsPlaying] = useState(false);
    const [tempUrl, setTempUrl] = useState<string | null>(null);
    const refAudio = useRef<HTMLAudioElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [createNewFile, {isLoading: isCreating}] = useCreateStreamFileMutation();


    function getAudioFromText(text: string) {
        if (text.length > 0) {
            setIsLoading(true);
            const url = `streamfile/tts/`;

            //get the audio file from the server
            api.post(url, {text}, {
                responseType: 'blob',
                transformResponse: res => res,
            }).then(res => {
                setIsLoading(false);
                setCurrentText(text);
                const blob = new Blob([res.data], { type: res.headers['content-type'] || 'audio/mpeg' });
                const audioUrl = URL.createObjectURL(blob);
                setTempUrl(audioUrl);
            });
        }
    }

    useEffect(() => {
        if (tempUrl) {
            refAudio.current = new Audio(tempUrl!);
            refAudio.current.play();
            setIsPlaying(true);
            refAudio.current.onended = () => setIsPlaying(false);
        }

        return () => {
            if (refAudio.current) {
                refAudio.current.pause();
                setIsLoading(false)
                refAudio.current.src = '';
                refAudio.current = null;
            }
            if (tempUrl) {
                URL.revokeObjectURL(tempUrl);
            }
        }
    }, [tempUrl]);

    function isDirty() {
        return text.trim() !== currentText.trim();
    }

    function handleClick() {
        if (isDirty()) {
            getAudioFromText(text);
        } else {
            if (refAudio.current) {
                if (isPlaying) {
                    refAudio.current.pause();
                } else {
                    refAudio.current.play();
                }
                setIsPlaying(!isPlaying);
            }
        }
    }

    async function handleAcceptOrRestore() {

        if(!tempUrl) return;

        if (isDirty()) {
            setText(currentText);
            return;
        }

        const response = await fetch(tempUrl);
        const blob = await response.blob();
        const file = new File([blob], `tts-${Date.now()}.mp3`, { type: 'audio/mpeg' });

        createNewFile({file, source: 'TTS', text: currentText}).unwrap()
            .then(newFile => {
                onChange(newFile.id);
                setTempUrl(null);
                toast.success('קובץ השמע נשמר בהצלחה');
            }).catch(err => {
                console.error('Error creating stream file:', err);
                toast.error('שגיאה בשמירת קובץ השמע', {
                    description: err.message ?? err.data.msg ?? 'שגיאה לא ידועה',
                });
            })
    }

    return (
        <div className="relative">
            <Textarea
                autoSize
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="pt-2 pb-8 text-sm"
                rows={4}
                placeholder="הזן כאן טקסט כדי להמיר אותו לשמע"
            />
            {(tempUrl || isDirty()) && (
                <span className="absolute end-4 space-x-2 bottom-4 bg-gray-200 rounded-full p-0.5">
                    <Button
                        disabled={isLoading || isCreating}
                        size="icon-xs"
                        variant="outline"
                        className="cursor-pointer rounded-full"
                        onClick={handleClick}
                    >
                        {isLoading && isDirty() && <Spinner/>}
                        {isDirty() && !isLoading && <PlayIcon className="h-4 w-4" />}
                        {!isDirty() ? (isPlaying ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />) : null}
                    </Button>

                    {tempUrl && (
                        <Button
                            disabled={isLoading || isCreating}
                            size="icon-xs"
                            variant="outline"
                            className="cursor-pointer rounded-full"
                            onClick={handleAcceptOrRestore}
                        >
                            {isCreating && <Spinner/>}
                            {(isDirty() && !isCreating)
                                ? <ArrowUturnLeftIcon className="h-4 w-4" />
                                : (!isCreating ? <CheckIcon className="h-4 w-4"/> : null)
                            }
                        </Button>
                    )}

                </span>
            )}
        </div>
    );
}

